import { Redirect, Route, Switch } from "react-router-dom";
import { Suspense, lazy, useState } from "react";

import AlertBox from "components/global/AlertBox";
import Loader from "components/global/Loader";
import Navbar from "components/global/Navbar";
import Onboarding from "views/Onboarding";
import ScrollToTop from "components/global/ScrollToTop";
import Spacer from "components/global/Spacer";
import SwitchUser from "views/SwitchUser";
import { connect } from "react-redux";
import styled from "styled-components";
import { useEffect } from "react";
import VerifyEmailPrompt from "components/global/VerifyEmailPrompt";
import NewSidebar from "components/global/sidebar/Sidebar";
import { getUserAccounts } from "api";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ChannelsProvider } from "ws/Channels";
// import WorkspaceTour from "views/guidedTour/WorkspaceTour";

const Brands = lazy(() => import("views/brands/Brands"));
const TeamMembers = lazy(() => import("views/teamMembers/TeamMembers"));
const UserAccount = lazy(() => import("views/userAccount/UserAccount"));
const Drafts = lazy(() => import("views/drafts/Drafts"));
const Posts = lazy(() => import("views/posts/Posts"));
const Plans = lazy(() => import("views/plans/PlansNew"));
const WorkspaceSetting = lazy(() =>
  import("views/workspace/WorkspaceSettings")
);
const Billing = lazy(() => import("views/billing/Billing"));
const DirectMessages = lazy(() =>
  import("views/directMessage/DirectMessageLayout")
);
const CommentLayout = lazy(() => import("views/comments/CommentLayout"));
const AiAssistant = lazy(() => import("views/aiAssistant/AiAssistantRoutes"));
const AnalyticsRoutes = lazy(() => import("views/analytics/AnalyticsRoutes"));
const Calendar = lazy(() => import("views/calendar/Calendar"));

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
      props.collapseSidebar ? "19.2rem" : "24rem"} auto;
  transition: all 0.2s ease-out;

  .dashboardContent {
    height: 100vh;
    overflow: auto;
    position: relative;

    > .alertWrapper {
      position: fixed;
      left: ${(props) => (props.collapseSidebar ? "19.2rem" : "24rem")};
      top: 7.2rem;
      width: ${(props) =>
        props.collapseSidebar
          ? "calc(100vw - 19.2rem)"
          : "calc(100vw - 24rem)"};
      z-index: 10;
    }
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingWrapper = styled.div`
  height: 100vh;
  width: 100%;

  img.gif {
    height: 100px;
  }
`

const Dashboard = ({ logged_in, user, user_token, active_account, sidebar_collapsed }) => {
  const history = useHistory();
  // eslint-disable-next-line
  const [_sidebarOpen, setSidebarOpen] = useState(false);
  const [hideOnboarding, setHideOnboarding] = useState(true);
  const [loading, setLoading] = useState(true);

  const setOnboardingStatus = async () => {
    if (user?.has_viewed_tooltips) return;

    setHideOnboarding(true);
  };

  const fetchUserAccounts = async () => {
    setLoading(true);
    try {
      const res = await getUserAccounts();

      if (res?.data?.status === true) {
        if (!res.data.data.length && !window.location.pathname.includes("accept-invite")) {
          history.push("/welcome");
          return;
        } else {
          setLoading(false);
          return;
        }
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }

  useEffect(() => {
    setOnboardingStatus();
    fetchUserAccounts();
    // eslint-disable-next-line
  }, [active_account?.id]);

  if (!logged_in) {
    return <Redirect to={`/sign-in?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`} />;
  }

  if (loading) {
    return (
      <LoadingWrapper className="flexColumn alignCenter justifyCenter">
        <Loader />
      </LoadingWrapper>
    );
  }

  return (
    <ChannelsProvider authToken={user_token} authUser={user}>
    <Wrapper collapseSidebar={sidebar_collapsed}>
      <Onboarding hide={hideOnboarding} setHide={setHideOnboarding} />
      <AlertBox />
      <NewSidebar />
      <div className="dashboardContent">
        <ScrollToTop />
        <Navbar setSidebarOpen={setSidebarOpen} />
        <SwitchUser />
        <VerifyEmailPrompt />
        {/* <WorkspaceTour /> */}
        <Suspense
          fallback={
            <div>
              <Spacer y={9.6} />
              <Loader />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path="/dashboard/"
              component={() => <Redirect to="/dashboard/posts" />}
            />
            <Route path="/dashboard/posts" component={Posts} />
            <Route path="/dashboard/brands" component={Brands} />
            <Route path="/dashboard/team-members" component={TeamMembers} />
            <Route path="/dashboard/profile" component={UserAccount} />
            <Route path="/dashboard/drafts" component={Drafts} />
            <Route path="/dashboard/plans" component={Plans} />
            <Route
              path="/dashboard/workspace-settings"
              component={WorkspaceSetting}
            />
            <Route path="/dashboard/billing" component={Billing} />
            <Route
              path="/dashboard/direct-messages"
              component={DirectMessages}
            />
            <Route path="/dashboard/comments" component={CommentLayout} />
            <Route path="/dashboard/ai-assistant" component={AiAssistant} />
            <Route path="/dashboard/analytics" component={AnalyticsRoutes} />
            <Route path="/dashboard/calendar" component={Calendar} />
            {/* No match */}
            <Route component={() => <Redirect to="/dashboard/posts" />} />
          </Switch>
        </Suspense>
      </div>
    </Wrapper>
    </ChannelsProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    logged_in: state.logged_in,
    // user_accounts: state.user_accounts,
    user: state.user,
    sidebar_collapsed: state.sidebar_collapsed,
    active_account: state.active_account,
    user_token: state.user_token,
  };
};

export default connect(mapStateToProps)(Dashboard);
